import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { TRANSLATIONS, CURRENCIES, LANGS } from "@/lib/i18n";
import { DEFAULT_LEASE_SETTINGS, computeMonthly, loadLeaseSettings } from "@/lib/lease";

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);
export const useT = () => {
  const { lang } = useContext(AppContext);
  return TRANSLATIONS[lang] || TRANSLATIONS.en;
};

const LS = {
  cart: "kc_cart",
  lang: "kc_lang",
  currency: "kc_currency",
  compare: "kc_compare",
};

export function AppProvider({ children }) {
  // Auth
  const [user, setUser] = useState(null); // null | user
  const [authLoading, setAuthLoading] = useState(true);

  // Cart
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS.cart) || "[]"); } catch { return []; }
  });

  // Compare list (hardware IDs, max 3)
  const [compare, setCompare] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS.compare) || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(LS.compare, JSON.stringify(compare)); }, [compare]);

  const toggleCompare = (hwId) => {
    setCompare((c) => {
      if (c.includes(hwId)) return c.filter((x) => x !== hwId);
      if (c.length >= 3) return c; // max 3
      return [...c, hwId];
    });
  };
  const removeCompare = (hwId) => setCompare((c) => c.filter((x) => x !== hwId));
  const clearCompare = () => setCompare([]);

  // Language, auto-detect from browser on first visit if no preference set
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem(LS.lang);
    if (stored) return stored;
    // 1) URL param wins (e.g. ?lang=de)
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get("lang");
    const SUPPORTED = ["nl", "en", "de", "fr", "es", "it", "da"];
    if (urlLang && SUPPORTED.includes(urlLang)) {
      sessionStorage.setItem("kc_lang_auto", urlLang);
      return urlLang;
    }
    // 2) Browser language (navigator.language)
    const browser = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(browser) && browser !== "nl") {
      sessionStorage.setItem("kc_lang_auto", browser);
      return browser;
    }
    return "nl";
  });
  const [autoDetectedLang, setAutoDetectedLang] = useState(() => sessionStorage.getItem("kc_lang_auto"));
  const dismissAutoDetect = () => { sessionStorage.removeItem("kc_lang_auto"); setAutoDetectedLang(null); };
  // Currency
  const [currency, setCurrency] = useState(() => localStorage.getItem(LS.currency) || "EUR");
  const [rates, setRates] = useState({ EUR: 1, USD: 1.10, GBP: 0.86, CHF: 0.96 });

  useEffect(() => { localStorage.setItem(LS.cart, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(LS.lang, lang); }, [lang]);
  useEffect(() => { localStorage.setItem(LS.currency, currency); }, [currency]);

  // Fetch user on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch { setUser(null); }
      finally { setAuthLoading(false); }
    })();
  }, []);

  // Fetch rates
  useEffect(() => {
    api.get("/currency/rates").then(({ data }) => {
      if (data?.rates) setRates(data.rates);
    }).catch(() => {});
  }, []);

  // Fetch lease engine settings (single source for all monthly calcs)
  const [leaseSettings, setLeaseSettings] = useState(DEFAULT_LEASE_SETTINGS);
  useEffect(() => { loadLeaseSettings().then(setLeaseSettings); }, []);
  const monthlyFor = (investmentEUR) => computeMonthly(investmentEUR, leaseSettings).monthly;

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  // Cart helpers
  const addToCart = (item) => {
    setCart((c) => {
      const idx = c.findIndex(
        (x) => x.type === item.type && x.ref_id === item.ref_id && x.pricing_mode === item.pricing_mode
      );
      if (idx >= 0) {
        const next = [...c];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + (item.quantity || 1) };
        return next;
      }
      return [...c, { quantity: 1, ...item }];
    });
  };
  const removeFromCart = (idx) => setCart((c) => c.filter((_, i) => i !== idx));
  const updateQty = (idx, q) => setCart((c) => c.map((x, i) => (i === idx ? { ...x, quantity: Math.max(1, q) } : x)));
  const updatePricingMode = (idx, pricing_mode, unit_price, monthly_price) =>
    setCart((c) => c.map((x, i) => (i === idx ? { ...x, pricing_mode, unit_price, monthly_price } : x)));
  const clearCart = () => setCart([]);

  // Currency conversion (EUR → target); keep exact so cart matches the charged amount
  const toDisplay = (amountEUR) => {
    const r = rates[currency] ?? 1;
    return Math.round(amountEUR * r * 100) / 100;
  };
  const symbolOf = (code) => (CURRENCIES.find((c) => c.code === code) || {}).symbol || code;
  const formatPrice = (amountEUR) => {
    if (amountEUR === null || amountEUR === undefined || amountEUR === "") return "";
    const val = toDisplay(amountEUR);
    const s = symbolOf(currency);
    const hasCents = Math.abs(val - Math.round(val)) > 0.001;
    const locale = lang === "nl" ? "nl-NL" : lang === "de" ? "de-DE" : lang === "fr" ? "fr-FR" : "en-GB";
    return `${s} ${val.toLocaleString(locale, { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 })}`;
  };

  const value = {
    user, authLoading, login, register, logout, setUser,
    cart, addToCart, removeFromCart, updateQty, updatePricingMode, clearCart,
    compare, toggleCompare, removeCompare, clearCompare,
    lang, setLang, LANGS, autoDetectedLang, dismissAutoDetect,
    currency, setCurrency, CURRENCIES, rates, formatPrice, toDisplay, symbolOf,
    leaseSettings, monthlyFor,
    formatApiError,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
